/* eslint-disable */
import { Box, Clock, Zap, CheckCircle2, AlertTriangle, PauseCircle, Sparkles } from "lucide-react";
import React, { useState, useEffect } from "react";

export default function ExecutionQueue({ setPage }) {
  // --- REAL-TIME AUTO REFRESH & TOAST STATE ---
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(new Date().toLocaleTimeString());
  const [toast, setToast] = useState(null);

  // --- SELECTION & DRAWER STATE ---
  const [selectedJob, setSelectedJob] = useState(null);
  const [selectedJobIds, setSelectedJobIds] = useState([]);
  const [drawerTab, setDrawerTab] = useState("timeline"); // timeline | config | metrics | artifacts | logs
  const [logTab, setLogTab] = useState("execution"); // execution | browser | network | system | ai

  // --- MODALS ---
  const [showNewModal, setShowNewModal] = useState(false);
  const [newExecutionForm, setNewExecutionForm] = useState({
    name: "Regression Test Suite - Lead Ingestion",
    project: "CRM Platform",
    application: "Sales Management",
    environment: "Staging",
    browser: "Chrome 124",
    os: "Windows 11",
    worker: "Worker Node #1 (US-East)",
    priority: "High",
    type: "Automated Suite"
  });

  // --- FILTERS STATE ---
  const [showFilterBar, setShowFilterBar] = useState(true);
  const [filters, setFilters] = useState({
    search: "",
    project: "All",
    application: "All",
    environment: "All",
    browser: "All",
    os: "All",
    status: "All",
    priority: "All",
    worker: "All",
    type: "All"
  });

  // --- MOCK INITIAL JOBS ---
  const [jobs, setJobs] = useState([
    {
      id: "EXEC-8901",
      name: "E2E Checkout Flow & Stripe Payment",
      project: "CRM Platform",
      application: "Sales Portal",
      environment: "Staging",
      triggeredBy: "ManiR (CI/CD Pipeline)",
      browser: "Chrome 124",
      os: "Windows 11",
      worker: "Worker Node #1 (US-East)",
      priority: "Critical",
      status: "Running",
      progress: 68,
      currentStep: "Executing Step 12/18: Validating Payment Response Token",
      queuedTime: "10:14:02 AM",
      startedTime: "10:14:08 AM",
      estCompletion: "10:15:30 AM",
      duration: "1m 12s",
      type: "Automated Regression",
      vus: 50,
      passed: 11,
      failed: 0,
      skipped: 0,
      cpu: "42%",
      memory: "1.4 GB",
      network: "14.2 MB/s"
    },
    {
      id: "EXEC-8902",
      name: "User Login & MFA Validation Suite",
      project: "Banking Portal",
      application: "Auth Service",
      environment: "QA",
      triggeredBy: "Scheduled Cron (Hourly)",
      browser: "Firefox 125",
      os: "Ubuntu 22.04",
      worker: "Worker Node #2 (US-West)",
      priority: "High",
      status: "Running",
      progress: 45,
      currentStep: "Executing Step 5/11: Verifying OTP Code Dispatch",
      queuedTime: "10:14:15 AM",
      startedTime: "10:14:22 AM",
      estCompletion: "10:16:00 AM",
      duration: "48s",
      type: "Smoke Suite",
      vus: 20,
      passed: 4,
      failed: 0,
      skipped: 0,
      cpu: "28%",
      memory: "880 MB",
      network: "8.5 MB/s"
    },
    {
      id: "EXEC-8903",
      name: "Database Heavy Query Load Test",
      project: "Analytics Engine",
      application: "Data Warehouse",
      environment: "Staging",
      triggeredBy: "ManiR",
      browser: "Headless Chrome",
      os: "Linux Kernel 6.1",
      worker: "Worker Node #4 (AP-South)",
      priority: "Critical",
      status: "Queued",
      progress: 0,
      currentStep: "Waiting for idle worker node allocation...",
      queuedTime: "10:15:00 AM",
      startedTime: "-",
      estCompletion: "10:18:00 AM",
      duration: "-",
      type: "Load Test",
      vus: 500,
      passed: 0,
      failed: 0,
      skipped: 0,
      cpu: "0%",
      memory: "0 MB",
      network: "0 MB/s"
    },
    {
      id: "EXEC-8904",
      name: "Cross-Browser Responsive Layout Audit",
      project: "Education CRM",
      application: "Student Hub",
      environment: "Production",
      triggeredBy: "Scheduled Cron (Daily)",
      browser: "Safari 17",
      os: "macOS Sonoma",
      worker: "Worker Node #3 (EU-Central)",
      priority: "Medium",
      status: "Queued",
      progress: 0,
      currentStep: "Enqueued - Rank #2 in Queue",
      queuedTime: "10:15:10 AM",
      startedTime: "-",
      estCompletion: "10:19:30 AM",
      duration: "-",
      type: "Visual Regression",
      vus: 10,
      passed: 0,
      failed: 0,
      skipped: 0,
      cpu: "0%",
      memory: "0 MB",
      network: "0 MB/s"
    },
    {
      id: "EXEC-8905",
      name: "API Rate Limiting & Security Scan",
      project: "API Gateway",
      application: "OAuth2 Provider",
      environment: "QA",
      triggeredBy: "Security Agent Bot",
      browser: "API Client (No GUI)",
      os: "Alpine Linux",
      worker: "Worker Node #1 (US-East)",
      priority: "High",
      status: "Preparing",
      progress: 15,
      currentStep: "Spinning up sandbox containers and mounting TLS certs",
      queuedTime: "10:14:40 AM",
      startedTime: "10:15:12 AM",
      estCompletion: "10:17:10 AM",
      duration: "14s",
      type: "Security Audit",
      vus: 100,
      passed: 2,
      failed: 0,
      skipped: 0,
      cpu: "18%",
      memory: "420 MB",
      network: "2.1 MB/s"
    },
    {
      id: "EXEC-8906",
      name: "Lead Form Submission & Webhook Dispatch",
      project: "CRM Platform",
      application: "Lead Engine",
      environment: "Staging",
      triggeredBy: "GitHub Actions #412",
      browser: "Chrome 124",
      os: "Windows 11",
      worker: "Worker Node #2 (US-West)",
      priority: "Medium",
      status: "Completed",
      progress: 100,
      currentStep: "All 24 test assertions passed successfully",
      queuedTime: "10:05:00 AM",
      startedTime: "10:05:04 AM",
      estCompletion: "10:07:30 AM",
      duration: "2m 26s",
      type: "Functional Automation",
      vus: 1,
      passed: 24,
      failed: 0,
      skipped: 0,
      cpu: "0%",
      memory: "0 MB",
      network: "0 MB/s"
    },
    {
      id: "EXEC-8907",
      name: "Mobile PWA Offline Cache Audit",
      project: "Mobile App",
      application: "iOS PWA",
      environment: "QA",
      triggeredBy: "ManiR",
      browser: "Mobile Safari",
      os: "iOS 17.4",
      worker: "Worker Node #5 (SA-East)",
      priority: "Low",
      status: "Failed",
      progress: 82,
      currentStep: "Failed at Step 9: ServiceWorker Cache Storage Timeout",
      queuedTime: "09:50:12 AM",
      startedTime: "09:50:20 AM",
      estCompletion: "-",
      duration: "3m 45s",
      type: "Mobile Audit",
      vus: 5,
      passed: 8,
      failed: 1,
      skipped: 2,
      cpu: "0%",
      memory: "0 MB",
      network: "0 MB/s"
    },
    {
      id: "EXEC-8908",
      name: "Peak Traffic Spike Endurance Test",
      project: "CRM Platform",
      application: "API Ingestion",
      environment: "Staging",
      triggeredBy: "Load Test Agent",
      browser: "Headless Chrome",
      os: "Linux",
      worker: "Worker Node #4 (AP-South)",
      priority: "Critical",
      status: "Retrying",
      progress: 30,
      currentStep: "Attempt 2/3: Re-initializing load generators after timeout",
      queuedTime: "10:10:00 AM",
      startedTime: "10:12:00 AM",
      estCompletion: "10:20:00 AM",
      duration: "3m 15s",
      type: "Spike Test",
      vus: 2000,
      passed: 120,
      failed: 4,
      skipped: 0,
      cpu: "88%",
      memory: "6.2 GB",
      network: "45.0 MB/s"
    },
    {
      id: "EXEC-8909",
      name: "Role-Based Access Control Verification",
      project: "Admin Console",
      application: "RBAC Module",
      environment: "Production",
      triggeredBy: "ManiR",
      browser: "Edge 124",
      os: "Windows 11",
      worker: "Worker Node #3 (EU-Central)",
      priority: "High",
      status: "Paused",
      progress: 50,
      currentStep: "Execution paused by User ManiR at Step 6",
      queuedTime: "10:00:00 AM",
      startedTime: "10:01:00 AM",
      estCompletion: "-",
      duration: "1m 30s",
      type: "Security Test",
      vus: 1,
      passed: 6,
      failed: 0,
      skipped: 6,
      cpu: "2%",
      memory: "310 MB",
      network: "0.1 MB/s"
    },
    {
      id: "EXEC-8910",
      name: "Automated Daily Health Check",
      project: "Education CRM",
      application: "All Services",
      environment: "Production",
      triggeredBy: "Scheduled Cron",
      browser: "Chrome 124",
      os: "Ubuntu 22.04",
      worker: "Worker Node #1 (US-East)",
      priority: "Low",
      status: "Cancelled",
      progress: 10,
      currentStep: "Cancelled by System Administrator",
      queuedTime: "09:30:00 AM",
      startedTime: "09:30:05 AM",
      estCompletion: "-",
      duration: "12s",
      type: "Smoke Suite",
      vus: 1,
      passed: 1,
      failed: 0,
      skipped: 9,
      cpu: "0%",
      memory: "0 MB",
      network: "0 MB/s"
    }
  ]);

  // --- WORKERS STATUS MOCK ---
  const [workers] = useState([
    { name: "Worker Node #1 (US-East)", status: "Busy", cpu: 78, memory: 68, job: "EXEC-8901", queueSize: 12, health: "99.4%", version: "v2.8.4", state: "Online" },
    { name: "Worker Node #2 (US-West)", status: "Busy", cpu: 64, memory: 52, job: "EXEC-8902", queueSize: 8, health: "98.9%", version: "v2.8.4", state: "Online" },
    { name: "Worker Node #3 (EU-Central)", status: "Idle", cpu: 12, memory: 34, job: "None", queueSize: 0, health: "100%", version: "v2.8.4", state: "Idle" },
    { name: "Worker Node #4 (AP-South)", status: "Busy", cpu: 92, memory: 85, job: "EXEC-8908", queueSize: 18, health: "96.2%", version: "v2.8.3", state: "Online" },
    { name: "Worker Node #5 (SA-East)", status: "Online", cpu: 8, memory: 28, job: "None", queueSize: 0, health: "99.8%", version: "v2.8.4", state: "Idle" },
    { name: "Worker Node #6 (AF-South)", status: "Offline", cpu: 0, memory: 0, job: "None", queueSize: 0, health: "0%", version: "v2.8.1", state: "Offline" }
  ]);

  // --- REAL-TIME TICKER EFFECT ---
  useEffect(() => {
    let timer;
    if (autoRefresh) {
      timer = setInterval(() => {
        setLastUpdated(new Date().toLocaleTimeString());
        setJobs(prevJobs =>
          prevJobs.map(job => {
            if (job.status === "Running" && job.progress < 100) {
              const newProgress = Math.min(100, job.progress + Math.floor(Math.random() * 8) + 2);
              const isDone = newProgress >= 100;
              return {
                ...job,
                progress: newProgress,
                status: isDone ? "Completed" : "Running",
                currentStep: isDone
                  ? "Execution completed. All assertions verified."
                  : job.currentStep
              };
            }
            if (job.status === "Preparing" && job.progress < 100) {
              const newProgress = Math.min(100, job.progress + 15);
              return {
                ...job,
                progress: newProgress,
                status: newProgress >= 30 ? "Running" : "Preparing"
              };
            }
            return job;
          })
        );
      }, 3000);
    }
    return () => clearInterval(timer);
  }, [autoRefresh]);

  // --- TOAST NOTIFIER HELPER ---
  const triggerToast = (msg, type = "info") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  // --- HANDLERS ---
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedJobIds(filteredJobs.map(j => j.id));
    } else {
      setSelectedJobIds([]);
    }
  };

  const handleSelectOne = (id) => {
    if (selectedJobIds.includes(id)) {
      setSelectedJobIds(selectedJobIds.filter(i => i !== id));
    } else {
      setSelectedJobIds([...selectedJobIds, id]);
    }
  };

  const handlePauseJob = (jobId) => {
    setJobs(jobs.map(j => j.id === jobId ? { ...j, status: "Paused" } : j));
    triggerToast(`Execution ${jobId} has been paused`, "warning");
  };

  const handleResumeJob = (jobId) => {
    setJobs(jobs.map(j => j.id === jobId ? { ...j, status: "Running" } : j));
    triggerToast(`Execution ${jobId} resumed execution`, "success");
  };

  const handleRetryJob = (jobId) => {
    setJobs(jobs.map(j => j.id === jobId ? { ...j, status: "Preparing", progress: 0, currentStep: "Re-queuing and initializing test runner..." } : j));
    triggerToast(`Re-triggering execution ${jobId}...`, "info");
  };

  const handleCancelJob = (jobId) => {
    setJobs(jobs.map(j => j.id === jobId ? { ...j, status: "Cancelled", currentStep: "Cancelled by user" } : j));
    triggerToast(`Execution ${jobId} cancelled`, "error");
  };

  const handleMoveToTop = (jobId) => {
    const target = jobs.find(j => j.id === jobId);
    if (!target) return;
    const rest = jobs.filter(j => j.id !== jobId);
    setJobs([target, ...rest]);
    triggerToast(`Execution ${jobId} moved to rank #1 in Queue`, "success");
  };

  const handleCreateNewExecution = (e) => {
    e.preventDefault();
    const newId = `EXEC-${Math.floor(8910 + Math.random() * 100)}`;
    const newJobObj = {
      id: newId,
      name: newExecutionForm.name,
      project: newExecutionForm.project,
      application: newExecutionForm.application,
      environment: newExecutionForm.environment,
      triggeredBy: "ManiR (Manual Trigger)",
      browser: newExecutionForm.browser,
      os: newExecutionForm.os,
      worker: newExecutionForm.worker,
      priority: newExecutionForm.priority,
      status: "Queued",
      progress: 0,
      currentStep: "Enqueued in queue - Awaiting worker assignment",
      queuedTime: new Date().toLocaleTimeString(),
      startedTime: "-",
      estCompletion: "Calculating...",
      duration: "-",
      type: newExecutionForm.type,
      vus: 10,
      passed: 0,
      failed: 0,
      skipped: 0,
      cpu: "0%",
      memory: "0 MB",
      network: "0 MB/s"
    };
    setJobs([newJobObj, ...jobs]);
    setShowNewModal(false);
    triggerToast(`New Execution ${newId} submitted to Queue!`, "success");
  };

  // --- BULK HANDLERS ---
  const handleBulkAction = (actionType) => {
    if (selectedJobIds.length === 0) return;
    if (actionType === "pause") {
      setJobs(jobs.map(j => selectedJobIds.includes(j.id) ? { ...j, status: "Paused" } : j));
      triggerToast(`Paused ${selectedJobIds.length} selected executions`, "warning");
    } else if (actionType === "resume") {
      setJobs(jobs.map(j => selectedJobIds.includes(j.id) ? { ...j, status: "Running" } : j));
      triggerToast(`Resumed ${selectedJobIds.length} selected executions`, "success");
    } else if (actionType === "retry") {
      setJobs(jobs.map(j => selectedJobIds.includes(j.id) ? { ...j, status: "Preparing", progress: 0 } : j));
      triggerToast(`Re-triggered ${selectedJobIds.length} executions`, "info");
    } else if (actionType === "cancel") {
      setJobs(jobs.map(j => selectedJobIds.includes(j.id) ? { ...j, status: "Cancelled" } : j));
      triggerToast(`Cancelled ${selectedJobIds.length} selected executions`, "error");
    } else if (actionType === "deleteCompleted") {
      setJobs(jobs.filter(j => !(selectedJobIds.includes(j.id) && j.status === "Completed")));
      triggerToast(`Cleared completed jobs from queue selection`, "info");
    }
    setSelectedJobIds([]);
  };

  // --- FILTERED JOBS ---
  const filteredJobs = jobs.filter(job => {
    if (filters.search && !job.name.toLowerCase().includes(filters.search.toLowerCase()) && !job.id.toLowerCase().includes(filters.search.toLowerCase())) return false;
    if (filters.project !== "All" && job.project !== filters.project) return false;
    if (filters.application !== "All" && job.application !== filters.application) return false;
    if (filters.environment !== "All" && job.environment !== filters.environment) return false;
    if (filters.browser !== "All" && !job.browser.toLowerCase().includes(filters.browser.toLowerCase())) return false;
    if (filters.os !== "All" && !job.os.toLowerCase().includes(filters.os.toLowerCase())) return false;
    if (filters.status !== "All" && job.status !== filters.status) return false;
    if (filters.priority !== "All" && job.priority !== filters.priority) return false;
    if (filters.worker !== "All" && !job.worker.includes(filters.worker)) return false;
    if (filters.type !== "All" && job.type !== filters.type) return false;
    return true;
  });

  // --- COUNTS FOR SUMMARY ---
  const countTotal = jobs.length;
  const countWaiting = jobs.filter(j => j.status === "Queued" || j.status === "Preparing").length;
  const countRunning = jobs.filter(j => j.status === "Running" || j.status === "Retrying").length;
  const countCompleted = jobs.filter(j => j.status === "Completed").length;
  const countFailed = jobs.filter(j => j.status === "Failed" || j.status === "Timeout").length;
  const countCancelled = jobs.filter(j => j.status === "Cancelled" || j.status === "Paused").length;

  // --- CHIP STYLE HELPERS ---
  const getStatusBadge = (status) => {
    switch (status) {
      case "Running":
        return <span className="status-badge running-chip">⚡ Running</span>;
      case "Queued":
        return <span className="status-badge queued-chip">⏳ Queued</span>;
      case "Preparing":
        return <span className="status-badge preparing-chip">⚙️ Preparing</span>;
      case "Completed":
        return <span className="status-badge completed-chip">✓ Completed</span>;
      case "Failed":
        return <span className="status-badge failed-chip">✕ Failed</span>;
      case "Paused":
        return <span className="status-badge paused-chip">⏸ Paused</span>;
      case "Retrying":
        return <span className="status-badge retrying-chip">🔄 Retrying</span>;
      case "Cancelled":
        return <span className="status-badge cancelled-chip">🚫 Cancelled</span>;
      default:
        return <span className="status-badge idle-chip">{status}</span>;
    }
  };

  const getPriorityBadge = (prio) => {
    switch (prio) {
      case "Critical":
        return <span className="prio-badge prio-critical">🔥 Critical</span>;
      case "High":
        return <span className="prio-badge prio-high">🔺 High</span>;
      case "Medium":
        return <span className="prio-badge prio-medium">🔹 Medium</span>;
      case "Low":
        return <span className="prio-badge prio-low">▫️ Low</span>;
      default:
        return <span className="prio-badge">{prio}</span>;
    }
  };

  return (
    <div className="execution-queue-page" style={{ padding: "24px", textAlign: "left", background: "#f8fafc", minHeight: "100vh" }}>
      {/* INLINE CUSTOM CSS SCOPE */}
      <style>{`
        .execution-queue-page {
          font-family: 'Outfit', 'Inter', -apple-system, sans-serif;
        }
        .eq-header-title {
          font-size: 26px;
          font-weight: 700;
          color: #0f172a;
          margin: 0;
        }
        .eq-header-sub {
          color: #64748b;
          font-size: 13.5px;
          margin: 4px 0 0 0;
        }
        .eq-card {
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 14px;
          padding: 20px;
          box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
          transition: all 0.2s ease;
        }
        .eq-card:hover {
          box-shadow: 0 6px 18px rgba(253, 97, 1, 0.08);
          border-color: rgba(253, 97, 1, 0.25);
        }
        .summary-card-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
          gap: 16px;
          margin-bottom: 24px;
        }
        .status-badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 4px 10px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 600;
        }
        .running-chip { background: rgba(59, 130, 246, 0.1); color: #FD6101; border: 1px solid rgba(59, 130, 246, 0.2); }
        .queued-chip { background: rgba(245, 158, 11, 0.1); color: #d97706; border: 1px solid rgba(245, 158, 11, 0.2); }
        .preparing-chip { background: rgba(147, 51, 234, 0.1); color: #9333ea; border: 1px solid rgba(147, 51, 234, 0.2); }
        .completed-chip { background: rgba(16, 185, 129, 0.1); color: #059669; border: 1px solid rgba(16, 185, 129, 0.2); }
        .failed-chip { background: rgba(239, 68, 68, 0.1); color: #dc2626; border: 1px solid rgba(239, 68, 68, 0.2); }
        .paused-chip { background: rgba(100, 116, 139, 0.1); color: #475569; border: 1px solid rgba(100, 116, 139, 0.2); }
        .retrying-chip { background: rgba(217, 119, 6, 0.1); color: #b45309; border: 1px solid rgba(217, 119, 6, 0.2); }
        .cancelled-chip { background: rgba(148, 163, 184, 0.15); color: #64748b; border: 1px solid rgba(148, 163, 184, 0.3); }

        .prio-badge {
          display: inline-block;
          padding: 3px 8px;
          border-radius: 6px;
          font-size: 11.5px;
          font-weight: 600;
        }
        .prio-critical { background: #fee2e2; color: #991b1b; }
        .prio-high { background: #ffedd5; color: #c2410c; }
        .prio-medium { background: #e0e7ff; color: #3730a3; }
        .prio-low { background: #f1f5f9; color: #475569; }

        .eq-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 13px;
        }
        .eq-table th {
          background: #f8fafc;
          color: #475569;
          font-weight: 600;
          padding: 12px 14px;
          border-bottom: 1px solid #e2e8f0;
          text-align: left;
          position: sticky;
          top: 0;
          z-index: 10;
        }
        .eq-table td {
          padding: 14px;
          border-bottom: 1px solid #f1f5f9;
          color: #1e293b;
        }
        .eq-table tr:hover td {
          background: rgba(253, 97, 1, 0.03);
        }
        .btn-purple {
          background: #FD6101;
          color: #ffffff;
          border: none;
          padding: 9px 18px;
          border-radius: 8px;
          font-weight: 600;
          font-size: 13px;
          cursor: pointer;
          transition: all 0.2s;
          box-shadow: 0 4px 12px rgba(253, 97, 1, 0.25);
        }
        .btn-purple:hover {
          background: #e05300;
          transform: translateY(-1px);
          box-shadow: 0 6px 16px rgba(253, 97, 1, 0.35);
        }
        .btn-secondary-eq {
          background: #ffffff;
          border: 1px solid #cbd5e1;
          color: #334155;
          padding: 8px 14px;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }
        .btn-secondary-eq:hover {
          background: #f8fafc;
          border-color: #94a3b8;
        }
        .toast-banner {
          position: fixed;
          top: 20px;
          right: 20px;
          z-index: 9999;
          padding: 12px 20px;
          border-radius: 8px;
          font-weight: 600;
          font-size: 13px;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
          color: #fff;
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .toast-info { background: #3b82f6; }
        .toast-success { background: #10b981; }
        .toast-warning { background: #f59e0b; }
        .toast-error { background: #ef4444; }

        .drawer-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(15, 23, 42, 0.4);
          backdrop-filter: blur(4px);
          z-index: 2000;
          display: flex;
          justify-content: flex-end;
        }
        .drawer-content {
          width: 680px;
          max-width: 100%;
          background: #ffffff;
          height: 100%;
          box-shadow: -10px 0 30px rgba(0, 0, 0, 0.15);
          display: flex;
          flex-direction: column;
          animation: slideInRight 0.25s ease-out;
        }
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
      `}</style>

      {/* TOAST BANNER */}
      {toast && (
        <div className={`toast-banner toast-${toast.type}`}>
          <span>⚡ Qintra Queue System:</span>
          <span>{toast.msg}</span>
        </div>
      )}

      {/* ==================================================
          PAGE HEADER
      ================================================== */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "24px" }}>
        <div>
          <h1 className="eq-header-title">Execution Queue</h1>
          <p className="eq-header-sub">
            Monitor, manage and control all queued, running and completed executions across enterprise projects.
          </p>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          {/* AUTO REFRESH TOGGLE */}
          <div style={{ display: "flex", alignItems: "center", gap: "8px", background: "#ffffff", padding: "6px 12px", borderRadius: "8px", border: "1px solid #cbd5e1" }}>
            <span style={{ fontSize: "12px", fontWeight: "600", color: "#64748b" }}>Auto Refresh ({lastUpdated})</span>
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              style={{ cursor: "pointer", width: "16px", height: "16px", accentColor: "#FD6101" }}
            />
          </div>

          <button className="btn-secondary-eq" onClick={() => { setLastUpdated(new Date().toLocaleTimeString()); triggerToast("Queue data refreshed successfully", "success"); }}>
            🔄 Refresh Queue
          </button>

          <button className="btn-secondary-eq" onClick={() => setShowFilterBar(!showFilterBar)}>
            🔍 {showFilterBar ? "Hide Filters" : "Filter Queue"}
          </button>

          <button className="btn-secondary-eq" onClick={() => triggerToast("Exported queue logs to CSV", "info")}>
            📥 Export CSV
          </button>

          <button className="btn-purple" onClick={() => setShowNewModal(true)}>
            + New Execution
          </button>
        </div>
      </div>

      {/* ==================================================
          TOP SUMMARY CARDS (6 CARDS)
      ================================================== */}
      <div className="summary-card-grid">
        {/* Total Jobs */}
        <div className="eq-card" style={{ borderLeft: "4px solid #FD6101" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: "12.5px", color: "#64748b", fontWeight: "600" }}>Total Jobs</span>
            <Box size={18} color="#64748b" />
          </div>
          <div style={{ fontSize: "28px", fontWeight: "700", color: "#0f172a", marginTop: "4px" }}>{countTotal}</div>
          <div style={{ fontSize: "11.5px", color: "#10b981", marginTop: "6px", fontWeight: "500" }}>▲ 14% vs yesterday</div>
        </div>

        {/* Waiting */}
        <div className="eq-card" style={{ borderLeft: "4px solid #f59e0b" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: "12.5px", color: "#64748b", fontWeight: "600" }}>Waiting</span>
            <Clock size={18} color="#d97706" />
          </div>
          <div style={{ fontSize: "28px", fontWeight: "700", color: "#d97706", marginTop: "4px" }}>{countWaiting}</div>
          <div style={{ fontSize: "11.5px", color: "#d97706", marginTop: "6px", fontWeight: "500" }}>Avg Wait: 1.2s</div>
        </div>

        {/* Running */}
        <div className="eq-card" style={{ borderLeft: "4px solid #3b82f6" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: "12.5px", color: "#64748b", fontWeight: "600" }}>Running</span>
            <Zap size={18} color="#FD6101" />
          </div>
          <div style={{ fontSize: "28px", fontWeight: "700", color: "#FD6101", marginTop: "4px" }}>{countRunning}</div>
          <div style={{ fontSize: "11.5px", color: "#FD6101", marginTop: "6px", fontWeight: "500" }}>6 Worker Nodes Active</div>
        </div>

        {/* Completed */}
        <div className="eq-card" style={{ borderLeft: "4px solid #10b981" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: "12.5px", color: "#64748b", fontWeight: "600" }}>Completed</span>
            <CheckCircle2 size={18} color="#059669" />
          </div>
          <div style={{ fontSize: "28px", fontWeight: "700", color: "#059669", marginTop: "4px" }}>{countCompleted}</div>
          <div style={{ fontSize: "11.5px", color: "#10b981", marginTop: "6px", fontWeight: "500" }}>96.8% Success Rate</div>
        </div>

        {/* Failed */}
        <div className="eq-card" style={{ borderLeft: "4px solid #ef4444" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: "12.5px", color: "#64748b", fontWeight: "600" }}>Failed</span>
            <AlertTriangle size={18} color="#dc2626" />
          </div>
          <div style={{ fontSize: "28px", fontWeight: "700", color: "#dc2626", marginTop: "4px" }}>{countFailed}</div>
          <div style={{ fontSize: "11.5px", color: "#dc2626", marginTop: "6px", fontWeight: "500" }}>▼ 3% failure drop</div>
        </div>

        {/* Cancelled */}
        <div className="eq-card" style={{ borderLeft: "4px solid #94a3b8" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: "12.5px", color: "#64748b", fontWeight: "600" }}>Cancelled / Paused</span>
            <PauseCircle size={18} color="#64748b" />
          </div>
          <div style={{ fontSize: "28px", fontWeight: "700", color: "#64748b", marginTop: "4px" }}>{countCancelled}</div>
          <div style={{ fontSize: "11.5px", color: "#64748b", marginTop: "6px", fontWeight: "500" }}>User Aborted</div>
        </div>
      </div>

      {/* ==================================================
          QUEUE HEALTH & AI INSIGHTS DUAL SECTION
      ================================================== */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px", marginBottom: "24px" }}>
        {/* QUEUE HEALTH CARD */}
        <div className="eq-card">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
            <h3 style={{ margin: 0, fontSize: "15px", fontWeight: "700", color: "#0f172a" }}>🖥️ Queue Health & Utilization</h3>
            <span style={{ fontSize: "12px", background: "#dcfce7", color: "#15803d", padding: "3px 8px", borderRadius: "12px", fontWeight: "600" }}>Healthy • 99.8% SLA</span>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px", marginBottom: "16px" }}>
            <div style={{ background: "#f8fafc", padding: "10px", borderRadius: "8px" }}>
              <div style={{ fontSize: "11px", color: "#64748b" }}>Worker Utilization</div>
              <div style={{ fontSize: "18px", fontWeight: "700", color: "#FD6101" }}>76%</div>
            </div>
            <div style={{ background: "#f8fafc", padding: "10px", borderRadius: "8px" }}>
              <div style={{ fontSize: "11px", color: "#64748b" }}>Average Wait Time</div>
              <div style={{ fontSize: "18px", fontWeight: "700", color: "#0f172a" }}>1.2s</div>
            </div>
            <div style={{ background: "#f8fafc", padding: "10px", borderRadius: "8px" }}>
              <div style={{ fontSize: "11px", color: "#64748b" }}>Avg Execution Time</div>
              <div style={{ fontSize: "18px", fontWeight: "700", color: "#0f172a" }}>42s</div>
            </div>
          </div>

          <div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px", marginBottom: "6px" }}>
              <span style={{ color: "#475569" }}>Queue Capacity (50 / 200 Slots Occupied)</span>
              <span style={{ fontWeight: "700", color: "#FD6101" }}>25% Full</span>
            </div>
            <div style={{ height: "8px", background: "#e2e8f0", borderRadius: "4px", overflow: "hidden" }}>
              <div style={{ width: "25%", height: "100%", background: "#FD6101", borderRadius: "4px" }}></div>
            </div>
          </div>
        </div>

        {/* AI INSIGHTS CARD */}
        <div className="eq-card" style={{ background: "#fff7ed", border: "1px solid rgba(253, 97, 1, 0.25)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
            <h3 style={{ margin: 0, fontSize: "15px", fontWeight: "700", color: "#c2410c" }}><Sparkles size={16} style={{ display: "inline", marginRight: "6px", verticalAlign: "middle" }} /> Qintra Execution Intelligence</h3>
            <span style={{ fontSize: "11.5px", background: "#FD6101", color: "#ffffff", padding: "2px 8px", borderRadius: "10px", fontWeight: "600" }}>Live Predictive AI</span>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "8px", fontSize: "12.5px" }}>
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <span>💡</span>
              <span style={{ color: "#334155" }}><strong>Worker Recommendation:</strong> Shift 2 queued tasks from US-East to Worker Node #3 (EU-Central) to reduce wait time by 4.2s.</span>
            </div>
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <span>📊</span>
              <span style={{ color: "#334155" }}><strong>Flakiness Predictor:</strong> EXEC-8908 has an 82% probability of socket timeout due to API spike congestion.</span>
            </div>
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <span>⚡</span>
              <span style={{ color: "#334155" }}><strong>Parallel Execution Confidence Score:</strong> 94/100 (Optimal bandwidth available).</span>
            </div>
          </div>
        </div>
      </div>

      {/* ==================================================
          FILTER BAR
      ================================================== */}
      {showFilterBar && (
        <div className="eq-card" style={{ marginBottom: "20px", background: "#ffffff" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "12px" }}>
            {/* Search */}
            <div>
              <label style={{ fontSize: "11px", fontWeight: "700", color: "#64748b" }}>SEARCH</label>
              <input
                type="text"
                placeholder="Search ID, Name..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                style={{ width: "100%", padding: "7px 10px", borderRadius: "6px", border: "1px solid #cbd5e1", fontSize: "12.5px" }}
              />
            </div>

            {/* Project */}
            <div>
              <label style={{ fontSize: "11px", fontWeight: "700", color: "#64748b" }}>PROJECT</label>
              <select
                value={filters.project}
                onChange={(e) => setFilters({ ...filters, project: e.target.value })}
                style={{ width: "100%", padding: "7px 10px", borderRadius: "6px", border: "1px solid #cbd5e1", fontSize: "12.5px" }}
              >
                <option value="All">All Projects</option>
                <option value="CRM Platform">CRM Platform</option>
                <option value="Banking Portal">Banking Portal</option>
                <option value="Education CRM">Education CRM</option>
                <option value="Analytics Engine">Analytics Engine</option>
              </select>
            </div>

            {/* Status */}
            <div>
              <label style={{ fontSize: "11px", fontWeight: "700", color: "#64748b" }}>STATUS</label>
              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                style={{ width: "100%", padding: "7px 10px", borderRadius: "6px", border: "1px solid #cbd5e1", fontSize: "12.5px" }}
              >
                <option value="All">All Statuses</option>
                <option value="Running">Running</option>
                <option value="Queued">Queued</option>
                <option value="Preparing">Preparing</option>
                <option value="Completed">Completed</option>
                <option value="Failed">Failed</option>
                <option value="Paused">Paused</option>
                <option value="Retrying">Retrying</option>
                <option value="Cancelled">Cancelled</option>
              </select>
            </div>

            {/* Priority */}
            <div>
              <label style={{ fontSize: "11px", fontWeight: "700", color: "#64748b" }}>PRIORITY</label>
              <select
                value={filters.priority}
                onChange={(e) => setFilters({ ...filters, priority: e.target.value })}
                style={{ width: "100%", padding: "7px 10px", borderRadius: "6px", border: "1px solid #cbd5e1", fontSize: "12.5px" }}
              >
                <option value="All">All Priorities</option>
                <option value="Critical">Critical</option>
                <option value="High">High</option>
                <option value="Medium">Medium</option>
                <option value="Low">Low</option>
              </select>
            </div>

            {/* Worker */}
            <div>
              <label style={{ fontSize: "11px", fontWeight: "700", color: "#64748b" }}>WORKER NODE</label>
              <select
                value={filters.worker}
                onChange={(e) => setFilters({ ...filters, worker: e.target.value })}
                style={{ width: "100%", padding: "7px 10px", borderRadius: "6px", border: "1px solid #cbd5e1", fontSize: "12.5px" }}
              >
                <option value="All">All Workers</option>
                <option value="Worker Node #1">Worker Node #1</option>
                <option value="Worker Node #2">Worker Node #2</option>
                <option value="Worker Node #3">Worker Node #3</option>
                <option value="Worker Node #4">Worker Node #4</option>
              </select>
            </div>

            {/* Clear Filters */}
            <div style={{ display: "flex", alignItems: "flex-end" }}>
              <button
                className="btn-secondary-eq"
                onClick={() => setFilters({ search: "", project: "All", application: "All", environment: "All", browser: "All", os: "All", status: "All", priority: "All", worker: "All", type: "All" })}
                style={{ width: "100%", padding: "7px 10px" }}
              >
                🧹 Clear Filters
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ==================================================
          BULK QUEUE ACTIONS BAR
      ================================================== */}
      {selectedJobIds.length > 0 && (
        <div style={{ background: "#1e293b", color: "#fff", padding: "12px 20px", borderRadius: "10px", marginBottom: "16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: "13px", fontWeight: "600" }}>✓ {selectedJobIds.length} Execution(s) Selected</span>
          <div style={{ display: "flex", gap: "8px" }}>
            <button className="btn-secondary-eq" style={{ padding: "5px 10px", fontSize: "12px" }} onClick={() => handleBulkAction("pause")}>⏸ Pause Selected</button>
            <button className="btn-secondary-eq" style={{ padding: "5px 10px", fontSize: "12px" }} onClick={() => handleBulkAction("resume")}>▶ Resume Selected</button>
            <button className="btn-secondary-eq" style={{ padding: "5px 10px", fontSize: "12px" }} onClick={() => handleBulkAction("retry")}>🔄 Retry Selected</button>
            <button className="btn-secondary-eq" style={{ padding: "5px 10px", fontSize: "12px", background: "#ef4444", color: "#fff", borderColor: "#ef4444" }} onClick={() => handleBulkAction("cancel")}>🚫 Cancel Selected</button>
          </div>
        </div>
      )}

      {/* ==================================================
          MAIN EXECUTION TABLE
      ================================================== */}
      <div className="eq-card" style={{ padding: 0, overflow: "hidden" }}>
        {filteredJobs.length === 0 ? (
          /* EMPTY STATE */
          <div style={{ padding: "60px", textAlign: "center" }}>
            <div style={{ fontSize: "48px", marginBottom: "12px" }}>📭</div>
            <h3 style={{ margin: 0, fontSize: "18px", color: "#0f172a" }}>No executions available</h3>
            <p style={{ color: "#64748b", fontSize: "13.5px", marginTop: "6px" }}>No queued or active executions match your current filter parameters.</p>
            <button className="btn-purple" style={{ marginTop: "16px" }} onClick={() => setShowNewModal(true)}>
              + Create New Execution
            </button>
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table className="eq-table">
              <thead>
                <tr>
                  <th style={{ width: "40px", textAlign: "center" }}>
                    <input type="checkbox" onChange={handleSelectAll} checked={selectedJobIds.length === filteredJobs.length && filteredJobs.length > 0} />
                  </th>
                  <th>Queue ID</th>
                  <th>Execution Name</th>
                  <th>Project / App</th>
                  <th>Browser & OS</th>
                  <th>Worker Node</th>
                  <th>Priority</th>
                  <th>Status</th>
                  <th style={{ minWidth: "200px" }}>Execution Progress</th>
                  <th>Duration</th>
                  <th style={{ textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredJobs.map((job) => (
                  <tr key={job.id}>
                    <td style={{ textAlign: "center" }}>
                      <input type="checkbox" checked={selectedJobIds.includes(job.id)} onChange={() => handleSelectOne(job.id)} />
                    </td>

                    {/* Queue ID */}
                    <td style={{ fontWeight: "700", fontFamily: "monospace", color: "#FD6101" }}>
                      <span style={{ cursor: "pointer" }} onClick={() => setSelectedJob(job)}>
                        {job.id}
                      </span>
                    </td>

                    {/* Execution Name */}
                    <td>
                      <div style={{ fontWeight: "600", color: "#0f172a", cursor: "pointer" }} onClick={() => setSelectedJob(job)}>
                        {job.name}
                      </div>
                      <div style={{ fontSize: "11px", color: "#64748b", marginTop: "2px" }}>
                        Triggered by <strong>{job.triggeredBy}</strong>
                      </div>
                    </td>

                    {/* Project / App */}
                    <td>
                      <div style={{ fontWeight: "500" }}>{job.project}</div>
                      <div style={{ fontSize: "11px", color: "#64748b" }}>{job.application} ({job.environment})</div>
                    </td>

                    {/* Browser & OS */}
                    <td>
                      <div style={{ fontSize: "12.5px" }}>🌐 {job.browser}</div>
                      <div style={{ fontSize: "11px", color: "#64748b" }}>💻 {job.os}</div>
                    </td>

                    {/* Worker Node */}
                    <td>
                      <div style={{ fontSize: "12px", fontWeight: "500", color: "#334155" }}>
                        🛠 {job.worker}
                      </div>
                    </td>

                    {/* Priority */}
                    <td>{getPriorityBadge(job.priority)}</td>

                    {/* Status */}
                    <td>{getStatusBadge(job.status)}</td>

                    {/* Progress */}
                    <td>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: "11px", marginBottom: "4px" }}>
                        <span style={{ color: "#64748b" }}>{job.currentStep}</span>
                        <span style={{ fontWeight: "700", color: "#0f172a" }}>{job.progress}%</span>
                      </div>
                      <div style={{ height: "6px", background: "#e2e8f0", borderRadius: "3px", overflow: "hidden" }}>
                        <div
                          style={{
                            width: `${job.progress}%`,
                            height: "100%",
                            background: job.status === "Failed" ? "#ef4444" : job.status === "Completed" ? "#10b981" : "#FD6101",
                            transition: "width 0.3s ease"
                          }}
                        />
                      </div>
                    </td>

                    {/* Duration */}
                    <td style={{ fontSize: "12px", fontWeight: "500", color: "#475569" }}>
                      {job.duration}
                    </td>

                    {/* Actions */}
                    <td style={{ textAlign: "right" }}>
                      <div style={{ display: "flex", justifyContent: "flex-end", gap: "6px" }}>
                        <button
                          className="btn-secondary-eq"
                          style={{ padding: "4px 8px", fontSize: "11.5px" }}
                          onClick={() => setSelectedJob(job)}
                          title="View Details"
                        >
                          👁 Details
                        </button>

                        {job.status === "Running" && (
                          <button
                            className="btn-secondary-eq"
                            style={{ padding: "4px 8px", fontSize: "11.5px", color: "#d97706" }}
                            onClick={() => handlePauseJob(job.id)}
                            title="Pause Execution"
                          >
                            ⏸
                          </button>
                        )}

                        {job.status === "Paused" && (
                          <button
                            className="btn-secondary-eq"
                            style={{ padding: "4px 8px", fontSize: "11.5px", color: "#059669" }}
                            onClick={() => handleResumeJob(job.id)}
                            title="Resume Execution"
                          >
                            ▶
                          </button>
                        )}

                        {(job.status === "Failed" || job.status === "Cancelled") && (
                          <button
                            className="btn-secondary-eq"
                            style={{ padding: "4px 8px", fontSize: "11.5px", color: "#FD6101" }}
                            onClick={() => handleRetryJob(job.id)}
                            title="Retry Execution"
                          >
                            🔄
                          </button>
                        )}

                        {job.status === "Queued" && (
                          <button
                            className="btn-secondary-eq"
                            style={{ padding: "4px 8px", fontSize: "11.5px", color: "#FD6101" }}
                            onClick={() => handleMoveToTop(job.id)}
                            title="Move to Top"
                          >
                            ⬆️ Top
                          </button>
                        )}

                        <button
                          className="btn-secondary-eq"
                          style={{ padding: "4px 8px", fontSize: "11.5px", color: "#dc2626" }}
                          onClick={() => handleCancelJob(job.id)}
                          title="Cancel Execution"
                        >
                          ✕
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ==================================================
          ENTERPRISE WORKER MONITORING GRID
      ================================================== */}
      <div style={{ marginTop: "24px" }}>
        <h3 style={{ fontSize: "16px", fontWeight: "700", color: "#0f172a", marginBottom: "14px" }}>
          📡 Distributed Enterprise Worker Status Monitor
        </h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "16px" }}>
          {workers.map((w, idx) => (
            <div key={idx} className="eq-card" style={{ padding: "16px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                <strong style={{ fontSize: "13.5px", color: "#0f172a" }}>{w.name}</strong>
                <span style={{ fontSize: "11px", padding: "2px 8px", borderRadius: "10px", fontWeight: "700", background: w.state === "Online" || w.state === "Busy" ? "#dcfce7" : "#fee2e2", color: w.state === "Online" || w.state === "Busy" ? "#15803d" : "#991b1b" }}>
                  {w.state}
                </span>
              </div>
              <div style={{ fontSize: "12px", color: "#64748b", display: "flex", flexDirection: "column", gap: "4px" }}>
                <div>Current Task: <strong>{w.job}</strong></div>
                <div>CPU Usage: <strong>{w.cpu}%</strong> | Memory: <strong>{w.memory}%</strong></div>
                <div>Queue Capacity: <strong>{w.queueSize} Jobs Enqueued</strong></div>
                <div>Health Score: <strong>{w.health}</strong> ({w.version})</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ==================================================
          EXECUTION DETAILS PANEL (SLIDE-OVER DRAWER)
      ================================================== */}
      {selectedJob && (
        <div className="drawer-overlay" onClick={() => setSelectedJob(null)}>
          <div className="drawer-content" onClick={(e) => e.stopPropagation()}>
            {/* Drawer Header */}
            <div style={{ padding: "20px", borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <h2 style={{ margin: 0, fontSize: "18px", fontWeight: "700", color: "#0f172a" }}>{selectedJob.id}: {selectedJob.name}</h2>
                  {getStatusBadge(selectedJob.status)}
                </div>
                <div style={{ fontSize: "12px", color: "#64748b", marginTop: "4px" }}>
                  {selectedJob.project} • {selectedJob.environment} • Triggered by {selectedJob.triggeredBy}
                </div>
              </div>
              <button className="btn-secondary-eq" onClick={() => setSelectedJob(null)}>✕ Close</button>
            </div>

            {/* Drawer Navigation Tabs */}
            <div style={{ display: "flex", borderBottom: "1px solid #e2e8f0", background: "#f8fafc", padding: "0 20px" }}>
              {[
                { id: "timeline", label: "⏱ Timeline" },
                { id: "config", label: "⚙️ Config" },
                { id: "metrics", label: "📊 Metrics" },
                { id: "artifacts", label: "🖼 Artifacts" },
                { id: "logs", label: "📜 Live Logs" }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setDrawerTab(tab.id)}
                  style={{
                    padding: "12px 16px",
                    border: "none",
                    background: "transparent",
                    borderBottom: drawerTab === tab.id ? "3px solid #FD6101" : "3px solid transparent",
                    fontWeight: drawerTab === tab.id ? "700" : "500",
                    color: drawerTab === tab.id ? "#FD6101" : "#64748b",
                    cursor: "pointer",
                    fontSize: "13px"
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Drawer Body Scroll */}
            <div style={{ flex: 1, overflowY: "auto", padding: "20px" }}>

              {/* TAB 1: TIMELINE */}
              {drawerTab === "timeline" && (
                <div>
                  <h4 style={{ margin: "0 0 16px 0", fontSize: "14px", color: "#0f172a" }}>Execution Flow Timeline</h4>
                  <div style={{ display: "flex", flexDirection: "column", gap: "16px", borderLeft: "2px solid #FD6101", paddingLeft: "16px", marginLeft: "8px" }}>
                    <div>
                      <strong style={{ color: "#FD6101", fontSize: "13px" }}>✓ Queued in Queue</strong>
                      <div style={{ fontSize: "11.5px", color: "#64748b" }}>{selectedJob.queuedTime} • Enqueued by CI/CD</div>
                    </div>
                    <div>
                      <strong style={{ color: "#FD6101", fontSize: "13px" }}>✓ Worker Assigned</strong>
                      <div style={{ fontSize: "11.5px", color: "#64748b" }}>Assigned to {selectedJob.worker}</div>
                    </div>
                    <div>
                      <strong style={{ color: "#FD6101", fontSize: "13px" }}>✓ Headless Browser Started</strong>
                      <div style={{ fontSize: "11.5px", color: "#64748b" }}>Launched {selectedJob.browser} on {selectedJob.os}</div>
                    </div>
                    <div>
                      <strong style={{ color: "#FD6101", fontSize: "13px" }}>✓ Environment Ready</strong>
                      <div style={{ fontSize: "11.5px", color: "#64748b" }}>Endpoint {selectedJob.environment} reachable (HTTP 200 OK)</div>
                    </div>
                    <div>
                      <strong style={{ color: selectedJob.status === "Completed" ? "#10b981" : "#3b82f6", fontSize: "13px" }}>
                        {selectedJob.status === "Completed" ? "✓ Execution Completed" : "⚡ Running Tests..."}
                      </strong>
                      <div style={{ fontSize: "11.5px", color: "#64748b" }}>{selectedJob.currentStep}</div>
                    </div>
                  </div>
                </div>
              )}

              {/* TAB 2: CONFIGURATION */}
              {drawerTab === "config" && (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", fontSize: "12.5px" }}>
                  <div className="eq-card">
                    <strong style={{ color: "#0f172a" }}>Browser Settings</strong>
                    <p style={{ color: "#64748b", margin: "4px 0 0 0" }}>Browser: {selectedJob.browser}</p>
                    <p style={{ color: "#64748b", margin: "2px 0 0 0" }}>OS: {selectedJob.os}</p>
                    <p style={{ color: "#64748b", margin: "2px 0 0 0" }}>Viewport: 1920 x 1080 (Desktop)</p>
                  </div>
                  <div className="eq-card">
                    <strong style={{ color: "#0f172a" }}>Environment Settings</strong>
                    <p style={{ color: "#64748b", margin: "4px 0 0 0" }}>Target Env: {selectedJob.environment}</p>
                    <p style={{ color: "#64748b", margin: "2px 0 0 0" }}>Parallel Virtual Users: {selectedJob.vus}</p>
                    <p style={{ color: "#64748b", margin: "2px 0 0 0" }}>Timeout SLA: 300 seconds</p>
                  </div>
                </div>
              )}

              {/* TAB 3: METRICS */}
              {drawerTab === "metrics" && (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px" }}>
                  <div className="eq-card" style={{ textAlign: "center" }}>
                    <div style={{ fontSize: "11px", color: "#64748b" }}>CPU Usage</div>
                    <div style={{ fontSize: "22px", fontWeight: "700", color: "#FD6101" }}>{selectedJob.cpu}</div>
                  </div>
                  <div className="eq-card" style={{ textAlign: "center" }}>
                    <div style={{ fontSize: "11px", color: "#64748b" }}>Memory Usage</div>
                    <div style={{ fontSize: "22px", fontWeight: "700", color: "#3b82f6" }}>{selectedJob.memory}</div>
                  </div>
                  <div className="eq-card" style={{ textAlign: "center" }}>
                    <div style={{ fontSize: "11px", color: "#64748b" }}>Passed Tests</div>
                    <div style={{ fontSize: "22px", fontWeight: "700", color: "#10b981" }}>{selectedJob.passed}</div>
                  </div>
                </div>
              )}

              {/* TAB 4: ARTIFACTS */}
              {drawerTab === "artifacts" && (
                <div>
                  <h4 style={{ margin: "0 0 12px 0", fontSize: "14px" }}>Generated Test Artifacts</h4>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                    <div className="eq-card" style={{ textAlign: "center", cursor: "pointer" }} onClick={() => triggerToast("Opening full screenshot gallery", "info")}>
                      <div style={{ fontSize: "24px" }}>📸</div>
                      <strong style={{ fontSize: "12.5px" }}>Screenshots (14 Captured)</strong>
                    </div>
                    <div className="eq-card" style={{ textAlign: "center", cursor: "pointer" }} onClick={() => triggerToast("Downloading MP4 Execution Recording", "info")}>
                      <div style={{ fontSize: "24px" }}>🎥</div>
                      <strong style={{ fontSize: "12.5px" }}>Execution Video (.mp4)</strong>
                    </div>
                    <div className="eq-card" style={{ textAlign: "center", cursor: "pointer" }} onClick={() => triggerToast("Downloading HAR Network Log", "info")}>
                      <div style={{ fontSize: "24px" }}>🌐</div>
                      <strong style={{ fontSize: "12.5px" }}>HAR Network Trace</strong>
                    </div>
                    <div className="eq-card" style={{ textAlign: "center", cursor: "pointer" }} onClick={() => triggerToast("Opening HTML Execution Report", "info")}>
                      <div style={{ fontSize: "24px" }}>📄</div>
                      <strong style={{ fontSize: "12.5px" }}>Interactive HTML Report</strong>
                    </div>
                  </div>
                </div>
              )}

              {/* TAB 5: LOGS */}
              {drawerTab === "logs" && (
                <div>
                  <div style={{ display: "flex", gap: "8px", marginBottom: "12px" }}>
                    {["execution", "browser", "network", "system", "ai"].map(lTab => (
                      <button
                        key={lTab}
                        onClick={() => setLogTab(lTab)}
                        style={{
                          padding: "4px 10px",
                          borderRadius: "6px",
                          border: "none",
                          fontSize: "11.5px",
                          fontWeight: "600",
                          background: logTab === lTab ? "#FD6101" : "#e2e8f0",
                          color: logTab === lTab ? "#fff" : "#475569",
                          cursor: "pointer"
                        }}
                      >
                        {lTab.toUpperCase()}
                      </button>
                    ))}
                  </div>

                  <div style={{ background: "#0f172a", color: "#38bdf8", padding: "14px", borderRadius: "8px", fontFamily: "monospace", fontSize: "11.5px", minHeight: "220px" }}>
                    <div>[10:14:02] [INFO] Enqueued execution {selectedJob.id}</div>
                    <div>[10:14:08] [INFO] Allocated {selectedJob.worker}</div>
                    <div>[10:14:10] [INFO] Headless Playwright Chrome session active</div>
                    <div>[10:14:15] [LOG] {selectedJob.currentStep}</div>
                    <div>[10:14:20] [DEBUG] HTTP 200 OK - Latency: 142ms</div>
                    {selectedJob.status === "Failed" && <div style={{ color: "#ef4444" }}>[ERROR] Timeout waiting for locator '#btn-submit'</div>}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ==================================================
          NEW EXECUTION MODAL
      ================================================== */}
      {showNewModal && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)", zIndex: 3000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: "#fff", width: "520px", borderRadius: "14px", padding: "24px", textAlign: "left" }}>
            <h3 style={{ margin: "0 0 16px 0", fontSize: "18px", color: "#0f172a" }}>+ Trigger New Execution</h3>
            <form onSubmit={handleCreateNewExecution} style={{ display: "flex", flexDirection: "column", gap: "12px", fontSize: "13px" }}>
              <div>
                <label style={{ fontWeight: "600", display: "block", marginBottom: "4px" }}>Execution Name</label>
                <input
                  type="text"
                  value={newExecutionForm.name}
                  onChange={(e) => setNewExecutionForm({ ...newExecutionForm, name: e.target.value })}
                  style={{ width: "100%", padding: "8px", borderRadius: "6px", border: "1px solid #cbd5e1" }}
                  required
                />
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                <div>
                  <label style={{ fontWeight: "600", display: "block", marginBottom: "4px" }}>Project</label>
                  <select
                    value={newExecutionForm.project}
                    onChange={(e) => setNewExecutionForm({ ...newExecutionForm, project: e.target.value })}
                    style={{ width: "100%", padding: "8px", borderRadius: "6px", border: "1px solid #cbd5e1" }}
                  >
                    <option>CRM Platform</option>
                    <option>Banking Portal</option>
                    <option>Education CRM</option>
                    <option>Analytics Engine</option>
                  </select>
                </div>

                <div>
                  <label style={{ fontWeight: "600", display: "block", marginBottom: "4px" }}>Environment</label>
                  <select
                    value={newExecutionForm.environment}
                    onChange={(e) => setNewExecutionForm({ ...newExecutionForm, environment: e.target.value })}
                    style={{ width: "100%", padding: "8px", borderRadius: "6px", border: "1px solid #cbd5e1" }}
                  >
                    <option>Staging</option>
                    <option>QA</option>
                    <option>Production</option>
                  </select>
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                <div>
                  <label style={{ fontWeight: "600", display: "block", marginBottom: "4px" }}>Browser</label>
                  <select
                    value={newExecutionForm.browser}
                    onChange={(e) => setNewExecutionForm({ ...newExecutionForm, browser: e.target.value })}
                    style={{ width: "100%", padding: "8px", borderRadius: "6px", border: "1px solid #cbd5e1" }}
                  >
                    <option>Chrome 124</option>
                    <option>Firefox 125</option>
                    <option>Safari 17</option>
                    <option>Edge 124</option>
                  </select>
                </div>

                <div>
                  <label style={{ fontWeight: "600", display: "block", marginBottom: "4px" }}>Priority</label>
                  <select
                    value={newExecutionForm.priority}
                    onChange={(e) => setNewExecutionForm({ ...newExecutionForm, priority: e.target.value })}
                    style={{ width: "100%", padding: "8px", borderRadius: "6px", border: "1px solid #cbd5e1" }}
                  >
                    <option>Critical</option>
                    <option>High</option>
                    <option>Medium</option>
                    <option>Low</option>
                  </select>
                </div>
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "12px" }}>
                <button type="button" className="btn-secondary-eq" onClick={() => setShowNewModal(false)}>Cancel</button>
                <button type="submit" className="btn-purple">Submit to Queue</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

